import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../../ui/dialog";
import Button from "../../form/button";
import { Input } from "../../ui/input";
import { IExpense, IExpenseListRecord, IExpenseTypeRecord, Status } from "../../../lib/types";
import pb from "../../../lib/pocketbase";
import ErrorMessage from "../../form/errorMessage";
import SelectType from "./selectType";
import { Switch } from "../../ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";

interface NewExpenseProps {
    children: React.ReactNode;
    reload?: () => void;
    defaultList?: string;
}
export default function NewExpense({ children, reload, defaultList }: NewExpenseProps) {
    const [open, setOpen] = useState(false);
    const [types, setTypes] = useState<IExpenseTypeRecord[]>([]);
    const [addToList, setAddToList] = useState(false);
    const [lists, setLists] = useState<IExpenseListRecord[]>([]);
    const [name, setName] = useState("");
    const [price, setPrice] = useState<number>();
    const [amount, setAmount] = useState<number>(1);
    const [status, setStatus] = useState<Status>("none");
    const [error, setError] = useState("");
    const [type, setType] = useState<IExpenseTypeRecord>();
    const [date, setDate] = useState(new Date().toISOString());
    const [income, setIncome] = useState(false);
    const [list, setList] = useState<string>();

    async function loadTypes() {
        const t: IExpenseTypeRecord[] = await pb.collection("expenseType").getFullList();
        setType(t.find((t) => t.default));
        setTypes(t);
    }

    async function loadLists() {
        const l: IExpenseListRecord[] = await pb.collection("expenseList").getFullList({
            filter: "archive=false"
        });
        setLists(l);
    }

    function reset() {
        setName("");
        setPrice(undefined);
        setAmount(1);
        setType(types.find((t) => t.default));
        setDate(new Date().toISOString());
        setIncome(false);
        setAddToList(false);
        setList(undefined);
    }

    useEffect(() => {
        loadTypes();
        loadLists();
    }, []);

    function add() {
        if (!pb.authStore.model) return;
        if (!price) {
            setError("Fill in the price");
            return;
        }
        if (!type) {
            setError("Expense must have a category");
            return;
        }
        setStatus("loading");
        const data: IExpense = {
            name: name,
            amount: amount,
            price: price,
            type: type.id,
            user: pb.authStore.model.id,
            date: date,
            income: income,
            list: defaultList || list || ""
        };

        pb.collection("expense")
            .create(data)
            .then(() => {
                setTimeout(() => {
                    setStatus("success");
                    setError("");
                    reload && reload();
                    reset();
                }, 500);
            })
            .catch((err: Error) => {
                setStatus("error");
                setError(err.message);
            })
            .finally(() => {
                setTimeout(() => {
                    setStatus("none");
                }, 3000);
            });
    }
    return (
        <Dialog
            open={open}
            onOpenChange={(x) => {
                setOpen(x);
                reset();
            }}>
            <DialogTrigger asChild>{children}</DialogTrigger>
            <DialogContent className="mt-8 md:mt-0">
                <DialogHeader>
                    <DialogTitle>Add expense</DialogTitle>
                </DialogHeader>
                <ErrorMessage error={error} close={() => setError("")} />

                <Input value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder="Name of the expense" />
                <div className="grid grid-cols-2 gap-2">
                    <div className="flex items-center">
                        <div className="flex h-9 w-12 items-center justify-center rounded-l border text-slate-500">€</div>
                        <Input min={0} className="rounded-l-none" type="number" value={price || ""} onChange={(e) => setPrice(parseFloat(e.target.value))} onKeyDown={(e) => e.key === "Enter" && add()} placeholder="Price" />
                    </div>
                    <div className="flex items-center">
                        <Input min={0} className="rounded-r-none" type="number" value={amount} onChange={(e) => setAmount(parseFloat(e.target.value))} onKeyDown={(e) => e.key === "Enter" && add()} placeholder="Amount" />
                        <div className="flex h-9 w-12 items-center justify-center rounded-r border text-slate-500">#</div>
                    </div>
                </div>
                <hr className="my-2" />
                <SelectType type={type} types={types} setType={(t) => setType(t)} />
                <Input className="" type="date" value={new Date(date)?.toISOString()?.substring(0, 10)} onChange={(e) => setDate(e.target.value)} />
                <p className="flex items-center gap-2">
                    This is an income: <Switch checked={income} onCheckedChange={setIncome} />
                </p>
                {!defaultList && (
                    <>
                        {!addToList ? (
                            <Button onClick={() => setAddToList(true)} className="w-fit">
                                Add to a list
                            </Button>
                        ) : (
                            <div>
                                <Select value={list || undefined} onValueChange={(l) => setList(l)}>
                                    <SelectTrigger className="w-full">
                                        <SelectValue placeholder="Select list" />
                                    </SelectTrigger>
                                    <SelectContent className="max-h-56">
                                        {lists?.map((list) => (
                                            <SelectItem value={list.id}>{list.name}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}
                    </>
                )}
                <DialogFooter className="gap-2">
                    <Button onClick={() => setOpen(false)}>Cancel</Button>
                    <Button status={status} onClick={() => add()} variant="dark">
                        {status === "loading" && "Adding"}
                        {status === "success" && "Added"}
                        {status === "error" && "Error"}
                        {status === "none" && "Add"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
